'use client';

import React, { useState, useMemo } from 'react';
import { useSearchParams } from 'next/navigation';
import {
  Search, SlidersHorizontal, X, ChevronDown, LayoutGrid, List,
  TrendingUp, Clock, Download, Zap } from 'lucide-react';
import ClipCard, { ClipData } from '@/components/ClipCard';
import { useClips } from '@/lib/useClips';
import CategoryIcon from '@/components/ui/CategoryIcon';

// ── Types ──────────────────────────────────────────────────────────────────────
type SortOption = 'trending' | 'newest' | 'most-downloaded' | 'most-liked';
type ViewMode = 'grid' | 'list';

interface Filters {
  resolution: string[];
  fps: string[];
  category: string;
  licenseType: string;
}

const CATEGORIES = [
  { key: 'all',          label: 'All' },
  { key: 'Action',       label: 'Action' },
  { key: 'Anime',        label: 'Anime' },
  { key: 'Cinematic',    label: 'Cinematic' },
  { key: 'Movies',       label: 'Movies' },
  { key: 'TV Shows',     label: 'TV Shows' },
  { key: 'Gaming',       label: 'Gaming' },
  { key: 'Cars',         label: 'Cars' },
  { key: 'Nature',       label: 'Nature' },
  { key: 'Sports',       label: 'Sports' },
  { key: 'Music Videos', label: 'Music' },
  { key: 'Memes',        label: 'Memes' },
  { key: 'VFX',          label: 'VFX' },
];

const SORT_OPTIONS: { value: SortOption; label: string; icon: React.ReactNode }[] = [
  { value: 'trending', label: 'Trending', icon: <TrendingUp size={14} /> },
  { value: 'newest', label: 'Newest', icon: <Clock size={14} /> },
  { value: 'most-downloaded', label: 'Most Downloaded', icon: <Download size={14} /> },
  { value: 'most-liked', label: 'Most Liked', icon: <Zap size={14} /> },
];

// ── Helpers ────────────────────────────────────────────────────────────────────
function matchesSearch(clip: ClipData, q: string): boolean {
  if (!q.trim()) return true;
  const lower = q.toLowerCase();
  return (
    clip.title.toLowerCase().includes(lower) ||
    clip.uploader.toLowerCase().includes(lower) ||
    (Array.isArray(clip.categories) ? clip.categories.some((cat) => cat.toLowerCase().includes(lower)) : false) ||
    clip.tags.some((t) => t.toLowerCase().includes(lower))
  );
}

function applyFilters(clips: ClipData[], filters: Filters): ClipData[] {
  return clips.filter((c) => {
    if (filters.resolution.length && !filters.resolution.includes(c.resolution)) return false;
    if (filters.fps.length) {
      const fpsMatch = filters.fps.some((f) => {
        if (f === '60fps') return c.fps >= 60;
        if (f === '120fps') return c.fps >= 120;
        return false;
      });
      if (!fpsMatch) return false;
    }
    if (filters.category && filters.category !== 'all') {
      const cats = Array.isArray(c.categories) ? c.categories : [(c as any).category];
      if (!cats.includes(filters.category)) return false;
    }
    if (filters.licenseType && c.licenseType !== filters.licenseType) return false;
    return true;
  });
}

function sortClips(clips: ClipData[], sort: SortOption): ClipData[] {
  const copy = [...clips];
  if (sort === 'newest') return copy.sort((a, b) => a.uploadedAt.localeCompare(b.uploadedAt));
  if (sort === 'most-downloaded') return copy.sort((a, b) => b.downloads - a.downloads);
  if (sort === 'most-liked') return copy.sort((a, b) => b.likes - a.likes);
  return copy.sort((a, b) => b.views - a.views); // trending
}

// ── FilterPill ─────────────────────────────────────────────────────────────────
function FilterPill({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150"
      style={{
        background: active ? 'var(--primary)' : 'var(--muted)',
        color: active ? '#fff' : 'var(--muted-foreground)',
        border: `1px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
      }}
    >
      {label}
    </button>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function DiscoverContent() {
  const searchParams = useSearchParams();
  const initialQuery = searchParams.get('q') ?? '';

  const [query, setQuery] = useState(initialQuery);
  const [sort, setSort] = useState<SortOption>('trending');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    resolution: [],
    fps: [],
    category: 'all',
    licenseType: '',
  });

  // Fetch all clips from backend
  const { clips: allClips, loading } = useClips({ sort });

  const activeFilterCount =
    filters.resolution.length +
    filters.fps.length +
    (filters.category !== 'all' ? 1 : 0) +
    (filters.licenseType ? 1 : 0);

  function toggleArr(arr: string[], val: string): string[] {
    return arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val];
  }

  const results = useMemo(() => {
    const searched = allClips.filter((c) => matchesSearch(c, query));
    const filtered = applyFilters(searched, filters);
    return sortClips(filtered, sort);
  }, [allClips, query, filters, sort]);

  const currentSortLabel = SORT_OPTIONS.find((s) => s.value === sort)?.label ?? 'Trending';

  return (
    <main className="max-w-screen-2xl mx-auto px-4 pb-28 pt-6">
      {/* ── Page heading ──────────────────────────────────────────────────────── */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight mb-1" style={{ color: 'var(--foreground)' }}>
          Discover Clips
        </h1>
        <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
          Search by title, creator, tags, category or resolution
        </p>
      </div>

      {/* ── Search bar ────────────────────────────────────────────────────────── */}
      <div className="relative mb-5">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }} />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search clips, creators, tags, categories…"
          className="input-field w-full pl-11 pr-11 py-3 text-sm"
        />
        {query && (
          <button onClick={() => setQuery('')} className="absolute right-4 top-1/2 -translate-y-1/2 transition-opacity hover:opacity-80" style={{ color: 'var(--muted-foreground)' }}>
            <X size={15} />
          </button>
        )}
      </div>

      {/* ── Category chips ────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 mb-5" style={{ scrollbarWidth: 'none' }}>
        {CATEGORIES.map((cat) => (
          <button
            key={cat.key}
            onClick={() => setFilters((f) => ({ ...f, category: cat.key }))}
            className="category-chip flex items-center gap-1.5 flex-shrink-0"
            style={filters.category === cat.key ? { background: 'var(--primary)', color: '#fff', borderColor: 'var(--primary)' } : {}}
          >
            <CategoryIcon category={cat.key} size={13} />
            {cat.label}
          </button>
        ))}
      </div>

      {/* ── Toolbar ───────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowFilters((v) => !v)}
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-all duration-150"
            style={{
              background: showFilters ? 'rgba(108,99,255,0.15)' : 'var(--muted)',
              color: showFilters ? 'var(--primary)' : 'var(--muted-foreground)',
              border: `1px solid ${showFilters ? 'rgba(108,99,255,0.4)' : 'var(--border)'}`,
            }}
          >
            <SlidersHorizontal size={14} />
            Filters
            {activeFilterCount > 0 && (
              <span className="w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center" style={{ background: 'var(--primary)', color: '#fff' }}>
                {activeFilterCount}
              </span>
            )}
          </button>

          {filters.resolution.map((r) => (
            <span key={r} className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold" style={{ background: 'rgba(59,158,255,0.12)', color: '#3B9EFF', border: '1px solid rgba(59,158,255,0.25)' }}>
              {r}
              <button onClick={() => setFilters((f) => ({ ...f, resolution: toggleArr(f.resolution, r) }))}><X size={10} /></button>
            </span>
          ))}
          {filters.fps.map((f) => (
            <span key={f} className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold" style={{ background: 'rgba(108,99,255,0.12)', color: '#8B84FF', border: '1px solid rgba(108,99,255,0.25)' }}>
              {f}
              <button onClick={() => setFilters((fi) => ({ ...fi, fps: toggleArr(fi.fps, f) }))}><X size={10} /></button>
            </span>
          ))}
          {filters.licenseType && (
            <span className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold" style={{ background: 'rgba(34,197,94,0.12)', color: '#22C55E', border: '1px solid rgba(34,197,94,0.25)' }}>
              {filters.licenseType}
              <button onClick={() => setFilters((f) => ({ ...f, licenseType: '' }))}><X size={10} /></button>
            </span>
          )}
          {activeFilterCount > 0 && (
            <button onClick={() => setFilters({ resolution: [], fps: [], category: 'all', licenseType: '' })} className="text-xs font-medium transition-colors hover:opacity-80" style={{ color: 'var(--muted-foreground)' }}>
              Clear all
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <button
              onClick={() => setShowSortMenu((v) => !v)}
              className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-all duration-150"
              style={{ background: 'var(--muted)', color: 'var(--muted-foreground)', border: '1px solid var(--border)' }}
            >
              {currentSortLabel}
              <ChevronDown size={13} />
            </button>
            {showSortMenu && (
              <div className="absolute right-0 top-full mt-1 z-30 rounded-xl overflow-hidden py-1 min-w-[160px]" style={{ background: 'var(--card)', border: '1px solid var(--border)', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
                {SORT_OPTIONS.map((opt) => (
                  <button key={opt.value} onClick={() => { setSort(opt.value); setShowSortMenu(false); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-left transition-colors hover:bg-muted" style={{ color: sort === opt.value ? 'var(--primary)' : 'var(--foreground)' }}>
                    {opt.icon}
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center rounded-xl overflow-hidden" style={{ border: '1px solid var(--border)', background: 'var(--muted)' }}>
            <button onClick={() => setViewMode('grid')} className="w-9 h-9 flex items-center justify-center transition-all duration-150" style={{ background: viewMode === 'grid' ? 'var(--primary)' : 'transparent', color: viewMode === 'grid' ? '#fff' : 'var(--muted-foreground)' }}>
              <LayoutGrid size={15} />
            </button>
            <button onClick={() => setViewMode('list')} className="w-9 h-9 flex items-center justify-center transition-all duration-150" style={{ background: viewMode === 'list' ? 'var(--primary)' : 'transparent', color: viewMode === 'list' ? '#fff' : 'var(--muted-foreground)' }}>
              <List size={15} />
            </button>
          </div>
        </div>
      </div>

      {/* ── Filter panel ──────────────────────────────────────────────────────── */}
      {showFilters && (
        <div className="rounded-2xl p-5 mb-6" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--muted-foreground)' }}>Resolution</p>
              <div className="flex flex-wrap gap-2">
                {['4K', '1080p', '720p'].map((r) => (
                  <FilterPill key={r} label={r} active={filters.resolution.includes(r)} onClick={() => setFilters((f) => ({ ...f, resolution: toggleArr(f.resolution, r) }))} />
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--muted-foreground)' }}>Frame Rate</p>
              <div className="flex flex-wrap gap-2">
                {['60fps', '120fps'].map((f) => (
                  <FilterPill key={f} label={f} active={filters.fps.includes(f)} onClick={() => setFilters((fi) => ({ ...fi, fps: toggleArr(fi.fps, f) }))} />
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--muted-foreground)' }}>License</p>
              <div className="flex flex-wrap gap-2">
                {['CC0', 'CC BY', 'CC BY-SA', 'Personal'].map((l) => (
                  <FilterPill key={l} label={l} active={filters.licenseType === l} onClick={() => setFilters((f) => ({ ...f, licenseType: f.licenseType === l ? '' : l }))} />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Results count ─────────────────────────────────────────────────────── */}
      {!loading && (
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm font-medium" style={{ color: 'var(--muted-foreground)' }}>
            {results.length === 0 ? 'No clips found' : `${results.length} clip${results.length !== 1 ? 's' : ''} found`}
            {query && <span style={{ color: 'var(--foreground)' }}> for <span className="font-semibold">"{query}"</span></span>}
          </p>
        </div>
      )}

      {/* ── Loading skeleton ──────────────────────────────────────────────────── */}
      {loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="rounded-2xl animate-pulse" style={{ background: 'var(--muted)', height: 220 }} />
          ))}
        </div>
      )}

      {/* ── Empty state ───────────────────────────────────────────────────────── */}
      {!loading && results.length === 0 && (
        <div className="flex flex-col items-center justify-center py-24 gap-4">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: 'var(--muted)' }}>
            <Search size={28} style={{ color: 'var(--muted-foreground)' }} />
          </div>
          <p className="text-lg font-semibold" style={{ color: 'var(--foreground)' }}>
            {query || activeFilterCount > 0 ? 'No clips match your search' : 'No clips yet'}
          </p>
          <p className="text-sm text-center max-w-xs" style={{ color: 'var(--muted-foreground)' }}>
            {query || activeFilterCount > 0
              ? 'Try adjusting your search or filters to find what you\'re looking for.'
              : 'Be the first creator to upload a clip!'}
          </p>
          {(query || activeFilterCount > 0) && (
            <button
              onClick={() => { setQuery(''); setFilters({ resolution: [], fps: [], category: 'all', licenseType: '' }); }}
              className="btn-primary px-5 py-2.5 text-sm mt-2"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* ── Results grid / list ───────────────────────────────────────────────── */}
      {!loading && results.length > 0 && (
        <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4' : 'flex flex-col gap-3'}>
          {results.map((clip) => <ClipCard key={clip.id} clip={clip} />)}
        </div>
      )}
    </main>
  );
}
